import AddIcon from '@mui/icons-material/Add';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    Menu,
    MenuItem,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import * as React from 'react';

import { ContextStore } from '../store/contextStore';
import ImportApiKeys from '../xml_import/ImportApiKeys';
import { useDeveloperApi } from './api';
import { API_KEY_COLUMNS, isRotatable, isSpent } from './apiKeyColumns';
import ApiKeyEditDialog from './ApiKeyEditDialog';
import type { ApiCredential } from './apiKeyTypes';
import { CopyButton, describeError, DeveloperShell, formatTs, mono, TableScroll, Timestamp, ToneChip } from './components';
import { API_KEY_SCOPES, apiKeyStatus, LEGACY_ACCESS_FLAGS } from './labels';
import { CredentialList, legacyImportKeyStatus } from './legacyImportKey';
import NewApiKeyDialog from './NewApiKeyDialog';
import RotateApiKeyDialog from './RotateApiKeyDialog';

type Credentials = CredentialList & { rows: ApiCredential[] | null };

const PAGE_SIZE = 100;

/** The tenant's API keys (GET /v2/edit/api_credential/ through the Developer API, so a 403 only leaves
 *  `rows` null and never signs the user out), for users who may list them (`enabled`). A franchise
 *  parent's selected licensee narrows the list, as the data provider's franchise wrapper did. */
const useApiCredentials = (enabled: boolean, licenseeId: number | null): [Credentials, () => void] => {
    const api = useDeveloperApi();
    const [list, setList] = React.useState<Credentials>({ loading: enabled, rows: null, complete: false });
    const [generation, setGeneration] = React.useState(0);

    React.useEffect(() => {
        if (!enabled) {
            setList({ loading: false, rows: null, complete: false });
            return undefined;
        }
        let active = true;
        api.apiCredentials({ page_size: PAGE_SIZE, franchise_to: licenseeId })
            .then((page) => {
                if (!active) {
                    return;
                }
                const results = Array.isArray(page?.results) ? page.results : null;
                const complete = !!results && (typeof page.count === 'number' ? results.length >= page.count : !page.next);
                setList({ loading: false, rows: results, complete });
            })
            .catch(() => active && setList({ loading: false, rows: null, complete: false }));
        return () => {
            active = false;
        };
    }, [api, enabled, licenseeId, generation]);

    return [list, React.useCallback(() => setGeneration((n) => n + 1), [])];
};

const HeaderCell = ({ label, help }: { label: string; help: string }) => (
    <TableCell sx={{ whiteSpace: 'nowrap' }}>
        {label}
        {/* Focusable, so the help is reachable from the keyboard too. */}
        <Tooltip title={help}>
            <Box component="span" tabIndex={0} aria-label={`${label}: ${help}`} sx={{ ml: 0.5, color: 'text.secondary', cursor: 'help' }}>
                <InfoOutlinedIcon sx={{ fontSize: 14, verticalAlign: 'text-top' }} />
            </Box>
        </Tooltip>
    </TableCell>
);

const Dash = () => <span>—</span>;

const Muted = ({ children }: { children: React.ReactNode }) => (
    <Typography variant="caption" color="text.secondary" component="div" sx={{ overflowWrap: 'anywhere' }}>{children}</Typography>
);

const StatusCell = ({ row }: { row: ApiCredential }) => {
    if (row.rotated_at && row.status === 'active') {
        return <ToneChip label="Rotated" tone="warning" hint={`Replaced by a new key; works until ${formatTs(row.expires)}, then revoked.`} />;
    }
    if (row.status === 'active' && isSpent(row)) {
        return <ToneChip label="Expired" tone="neutral" hint="Past its expiry: it no longer works." />;
    }
    const status = apiKeyStatus(row.status);
    return <ToneChip label={status.label} tone={status.tone} hint={status.hint} />;
};

interface RowActions {
    canWrite: boolean;
    onEdit: (row: ApiCredential) => void;
    onRotate: (row: ApiCredential) => void;
    onRevoke: (row: ApiCredential) => void;
}

const ActionsCell = ({ row, canWrite, onEdit, onRotate, onRevoke }: { row: ApiCredential } & RowActions) => {
    const [anchor, setAnchor] = React.useState<HTMLElement | null>(null);
    if (!canWrite || row.status === 'revoked') {
        return null;
    }
    const pick = (fn: (row: ApiCredential) => void) => () => {
        setAnchor(null);
        fn(row);
    };
    return (
        <>
            <IconButton size="small" aria-label={`actions for ${row.name}`} onClick={(ev) => setAnchor(ev.currentTarget)}>
                <MoreVertIcon fontSize="small" />
            </IconButton>
            <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)} sx={{ zIndex: 10002 }}>
                {!row.rotated_at && <MenuItem onClick={pick(onEdit)}>Edit</MenuItem>}
                {isRotatable(row) && <MenuItem onClick={pick(onRotate)}>Rotate</MenuItem>}
                {!row.in_use && <MenuItem onClick={pick(onRevoke)} sx={{ color: 'error.main' }}>Revoke</MenuItem>}
                {row.in_use && (
                    // Rotate and Revoke are Curated Planet's for a key something else manages: say why.
                    <MenuItem disabled data-testid="api-key-in-use-reason" sx={{ whiteSpace: 'normal', maxWidth: 320, typography: 'caption', opacity: '1 !important' }}>
                        {row.in_use_reason}
                    </MenuItem>
                )}
            </Menu>
        </>
    );
};

/** The tenant's API keys: every column with its help text (plan section 5). Access groups are read-only. */
const ApiCredentialTable = ({ rows, ...actions }: { rows: ApiCredential[] | null } & RowActions) => {
    if (!rows || rows.length === 0) {
        return null;
    }
    return (
        <Box sx={{ mt: 3 }} data-testid="api-credentials">
            <Typography variant="h6">API keys</Typography>
            <TableScroll>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            {API_KEY_COLUMNS.map((col) => <HeaderCell key={col.id} label={col.label} help={col.help} />)}
                            <TableCell aria-label="actions" />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((row) => {
                            const scope = API_KEY_SCOPES[row.scope];
                            const importKey = row.scope === 'tour_operator';
                            return (
                                <TableRow key={row.id} data-testid={`api-key-${row.id}`} sx={isSpent(row) ? { opacity: 0.6 } : undefined}>
                                    <TableCell sx={{ fontWeight: 500, minWidth: 140 }}>
                                        {row.name}
                                        {row.in_use && <Box sx={{ mt: 0.5 }}><ToneChip label="In use" tone="info" hint={row.in_use_reason || undefined} /></Box>}
                                    </TableCell>
                                    <TableCell sx={{ minWidth: 160, maxWidth: 280 }}>{row.description || <Dash />}</TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                        {scope ? <Tooltip title={scope.hint}><span>{scope.label}</span></Tooltip> : row.scope}
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={mono}>{row.key_prefix ? `${row.key_prefix}…` : '—'}</Box>
                                        {row.key_fingerprint && (
                                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                                <Tooltip title={row.key_fingerprint}><Box component="span" sx={{ ...mono, fontSize: 12, color: 'text.secondary' }}>{row.key_fingerprint.slice(0, 12)}</Box></Tooltip>
                                                <CopyButton value={row.key_fingerprint} label="fingerprint" />
                                            </Stack>
                                        )}
                                    </TableCell>
                                    <TableCell><StatusCell row={row} /></TableCell>
                                    <TableCell sx={{ minWidth: 150 }}>
                                        <Timestamp value={row.created_ts} />
                                        {row.created_by && <Muted>by {row.created_by.name || row.created_by.email}</Muted>}
                                        <Muted><Box component="span" sx={mono}>{row.created_ip || '—'}</Box></Muted>
                                    </TableCell>
                                    <TableCell sx={{ minWidth: 150 }}>
                                        <Timestamp value={row.last_used} />
                                        {row.last_used && <Muted><Box component="span" sx={mono}>{row.last_used_ip || '—'}</Box></Muted>}
                                    </TableCell>
                                    <TableCell>{row.expires ? <Timestamp value={row.expires} /> : 'Never'}</TableCell>
                                    <TableCell sx={{ minWidth: 140 }}>
                                        {importKey
                                            ? (row.legacy_access.length ? row.legacy_access.map((f) => LEGACY_ACCESS_FLAGS[f] || f).join(', ') : 'None')
                                            : <Dash />}
                                    </TableCell>
                                    <TableCell sx={mono}>
                                        {row.allowed_ips.length ? row.allowed_ips.map((ip) => <div key={ip}>{ip}</div>) : 'Any'}
                                    </TableCell>
                                    <TableCell sx={mono}>{row.user_agent_pattern || 'Any'}</TableCell>
                                    <TableCell>{row.access_groups_detail.length ? row.access_groups_detail.map((g) => g.name).join(', ') : <Dash />}</TableCell>
                                    <TableCell><ActionsCell row={row} {...actions} /></TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableScroll>
        </Box>
    );
};

/** Revoke is terminal: the key and its JWT tokens stop working at once and never work again. */
const RevokeDialog = ({ credential, onClose, onRevoked }: { credential: ApiCredential | null; onClose: () => void; onRevoked: () => void }) => {
    const api = useDeveloperApi();
    const [busy, setBusy] = React.useState(false);
    const [error, setError] = React.useState<unknown>(null);

    const revoke = async () => {
        if (!credential) {
            return;
        }
        setBusy(true);
        setError(null);
        try {
            await api.revokeApiCredential(credential.id);
            onRevoked();
            onClose();
        } catch (e) {
            setError(e);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog
            open={!!credential}
            onClose={busy ? undefined : onClose}
            aria-labelledby="revoke-api-key-title"
            TransitionProps={{ onExited: () => setError(null) }}
            sx={{ zIndex: 10002 }}
        >
            <DialogTitle id="revoke-api-key-title">{`Revoke "${credential?.name || ''}"?`}</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    Every request with this key, and with the JWT tokens issued for it, is refused from now on. This cannot be
                    undone: a revoked key never works again.
                </DialogContentText>
                {error ? <Alert severity="error" sx={{ mt: 2 }}>{describeError(error)}</Alert> : null}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={busy}>Cancel</Button>
                <Button variant="contained" color="error" onClick={revoke} disabled={busy} startIcon={busy ? <CircularProgress size={14} /> : undefined}>
                    Revoke
                </Button>
            </DialogActions>
        </Dialog>
    );
};

/** Developer -> API keys: the import keys page (moved from Settings, SBX D9) plus the tenant's API keys
 *  with their columns, New key, Edit, Rotate and Revoke (DSEC D1). The legacy access key shows only where it
 *  can authenticate (`legacyImportKeyStatus`). */
const ApiKeysPage = () => {
    const { hasPerm, environment, isFranchise, currentLicensee } = React.useContext(ContextStore) as any;
    // The backend's gates on /api_credential/: api_credential.list / .write; on a sandbox also
    // developer.keys_manage (its own tenant's tour_operator keys, the scope New key creates). On production
    // keys_manage counts for nothing (plan 7.5).
    const keysManage = environment === 'sandbox' && !!hasPerm?.('developer', 'keys_manage');
    const canList = keysManage || !!hasPerm?.('api_credential', 'list');
    const canWrite = keysManage || !!hasPerm?.('api_credential', 'write');
    const licenseeId = isFranchise && currentLicensee ? Number(currentLicensee) : null;
    const [credentials, reloadCredentials] = useApiCredentials(canList, licenseeId);
    const [creating, setCreating] = React.useState(false);
    const [editing, setEditing] = React.useState<ApiCredential | null>(null);
    const [rotating, setRotating] = React.useState<ApiCredential | null>(null);
    const [revoking, setRevoking] = React.useState<ApiCredential | null>(null);
    const reload = () => canList && reloadCredentials();

    const accessKeyStatus = (token: string) => legacyImportKeyStatus(token, { environment, isFranchise, list: credentials });

    return (
        <DeveloperShell
            title="API keys"
            actions={canWrite ? <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreating(true)}>New key</Button> : undefined}
        >
            <ImportApiKeys accessKeyStatus={accessKeyStatus} newKeyAvailable={canWrite} />
            {canList && (
                <ApiCredentialTable
                    rows={credentials.rows}
                    canWrite={canWrite}
                    onEdit={setEditing}
                    onRotate={setRotating}
                    onRevoke={setRevoking}
                />
            )}
            <NewApiKeyDialog open={creating} onClose={() => setCreating(false)} onCreated={reload} licenseeId={licenseeId} />
            <ApiKeyEditDialog credential={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} />
            <RotateApiKeyDialog credential={rotating} onClose={() => setRotating(null)} onRotated={reload} />
            <RevokeDialog credential={revoking} onClose={() => setRevoking(null)} onRevoked={reload} />
        </DeveloperShell>
    );
};

export default ApiKeysPage;
